import Highcharts from 'highcharts';
import { Theme } from '../../../../../../../theme/theme';
import { SM_BREAK } from '../RetirementChart.meta';

let verticalLabels: Record<string, Highcharts.SVGElement> = {};
let labelText: Highcharts.SVGElement;

interface RenderVerticalLabelOpts {
  chart: Highcharts.Chart;
  point: Highcharts.Point & { plotX?: number; plotY?: number }; // https://github.com/highcharts/highcharts/issues/13738
  id: string;
  value: number;
  label: string;
  offset: number;
  color: string;
  theme: Theme;
}

export const renderVerticalLabel = ({
  chart,
  point,
  id,
  value,
  label,
  offset,
  color,
  theme,
}: RenderVerticalLabelOpts) => {
  if (chart.chartWidth < SM_BREAK) {
    Object.values(verticalLabels).forEach(verticalLabel => verticalLabel.destroy());
    verticalLabels = {};

    return;
  }

  if (verticalLabels[id]) {
    verticalLabels[id].destroy();
  }

  verticalLabels[id] = chart.renderer
    .g(id)
    .attr({ zIndex: 3 + (offset === 0 ? 1 : 0) })
    .add();

  const x = chart.plotLeft + (point.plotX || 0);
  const fromY = chart.plotTop + (point.plotY || 0);
  const toY = chart.plotHeight + chart.plotTop + 30 + offset;

  chart.renderer
    .circle(x, fromY, 5)
    .attr({ fill: color })
    .add(verticalLabels[id]);

  const connector = chart.renderer
    .path(
      chart.renderer.crispLine(
        [
          ['M', x, fromY],
          ['L', x, toY],
        ],
        1,
        'round',
      ),
    )
    .attr({ stroke: color, 'stroke-width': 2, 'stroke-dasharray': '7 3' })
    .add(verticalLabels[id]);

  const labelGroup = chart.renderer.g('label').add(verticalLabels[id]);

  const labelBg = chart.renderer
    .rect(0, 0, 32, 20, 10)
    .attr({ fill: color })
    .align({ align: 'right', verticalAlign: 'bottom', x: -16 }, false, connector.getBBox())
    .add(labelGroup);

  chart.renderer
    .text(value.toString(), 0, 0)
    .attr({ 'text-anchor': 'middle' })
    .css({ color: theme.colors.bg, fontSize: '14px', fontWeight: '600' })
    .align({ align: 'center', verticalAlign: 'middle', y: 5 }, false, labelBg.getBBox())
    .add(labelGroup);

  labelText = chart.renderer
    .text(label, 0, 0)
    .attr({ 'text-anchor': 'end' })
    .css({ color: theme.colors.neutral['600'], fontSize: '12px', fontWeight: '600' })
    .align({ align: 'left', verticalAlign: 'middle', x: -10 }, false, labelGroup.getBBox())
    .css({ width: 110 })
    .add(verticalLabels[id]);

  // Annotation overflowing on the left side of the chart
  if (labelText.getBBox().x < 0) {
    labelText.destroy();
    labelText = chart.renderer
      .text(label, 0, 0)
      .attr({ 'text-anchor': 'start' })
      .css({ color: theme.colors.neutral['600'], fontSize: '12px', fontWeight: '600' })
      .align({ align: 'right', verticalAlign: 'middle', x: 10 }, false, labelGroup.getBBox())
      .css({ width: 110 })
      .add(verticalLabels[id]);
  }
};

export const removeVerticalLabel = (id: string) => {
  if (verticalLabels[id]) {
    verticalLabels[id].destroy();

    delete verticalLabels[id];
  }
};
